/**
 * Triangle Component
 * 
 * A simplified wrapper for rendering 2D triangles with fixed optimal settings.
 * 
 * Only accepts dimension values (for labels), not positions.
 * All positioning and styling are predefined for consistency.
 * 
 * Uses SVG for rendering, similar to polygon rendering in GraphRenderer.
 * Supports different triangle types: equilateral, isosceles, right, scalene
 */

import React from 'react';
import { Box } from '@mui/material';

interface TriangleProps {
  base?: number; // Base dimension (for label display)
  height?: number; // Height dimension (for label display)
  side?: number; // Side length for equilateral triangles (optional)
  hypotenuse?: number; // Hypotenuse length for right triangles (optional)
  unit?: string; // Unit of measurement (default: 'cm')
  triangleType?: 'equilateral' | 'isosceles' | 'right' | 'scalene'; // Type of triangle
  color?: string; // Fill color (default: '#e0e7ff')
  strokeColor?: string; // Stroke color (default: '#4f46e5')
  withAngles?: boolean; // Whether to display angle labels
  angles?: { angle1?: number; angle2?: number; angle3?: number }; // Angle values in degrees
  showHypotenuse?: boolean; // Whether to show hypotenuse instead of base/height for right triangles
  /** When false, hide side labels (base/height/side/hypotenuse). Use for "angles only" problems. */
  showSideLabels?: boolean;
}

/**
 * Triangle Component
 * 
 * Renders a 2D triangle with fixed, optimal positioning and labeling.
 * The triangle is always rendered at a normalized size for visual consistency.
 * The actual dimension values are only displayed in the labels.
 * 
 * @param base - Base dimension (default: 6)
 * @param height - Height dimension (default: 5)
 * @param side - Side length for equilateral triangles (optional)
 * @param unit - Unit of measurement (default: 'cm')
 * @param triangleType - Type of triangle (default: 'scalene')
 * @param color - Fill color in any CSS format (default: '#e0e7ff')
 * @param strokeColor - Stroke color in any CSS format (default: '#4f46e5')
 */
export const Triangle: React.FC<TriangleProps> = ({
  base = 6,
  height = 5,
  side,
  hypotenuse,
  unit = 'cm',
  triangleType = 'scalene',
  color = '#e0e7ff',
  strokeColor = '#4f46e5',
  withAngles = false,
  angles,
  showHypotenuse = false,
  showSideLabels = true
}) => {
  // Fixed normalized size for visual consistency
  // Actual dimension values are only shown in labels
  const normalizedBase = 35; // Base in SVG units (viewBox is 0 0 100 100)
  const normalizedHeight = 30; // Height in SVG units
  
  // Center the triangle in the viewBox
  const centerX = 50;
  const centerY = 50;
  const halfBase = normalizedBase / 2;
  
  // Calculate triangle points based on type
  let x1: number, y1: number, x2: number, y2: number, x3: number, y3: number;
  
  if (triangleType === 'equilateral') {
    // Equilateral triangle: all sides equal, centered
    const sideLength = normalizedBase;
    const h = (sideLength * Math.sqrt(3)) / 2;
    x1 = centerX;
    y1 = centerY - (h * 2) / 3; // Top vertex
    x2 = centerX - halfBase;
    y2 = centerY + h / 3; // Bottom left
    x3 = centerX + halfBase;
    y3 = centerY + h / 3; // Bottom right
  } else if (triangleType === 'right') {
    // Right triangle: right angle at bottom left
    x1 = centerX - halfBase;
    y1 = centerY + normalizedHeight / 2; // Bottom left (right angle)
    x2 = centerX + halfBase;
    y2 = centerY + normalizedHeight / 2; // Bottom right
    x3 = centerX - halfBase;
    y3 = centerY - normalizedHeight / 2; // Top left
  } else if (triangleType === 'isosceles') {
    // Isosceles triangle: two equal sides, apex at top
    x1 = centerX;
    y1 = centerY - normalizedHeight / 2; // Top vertex (apex)
    x2 = centerX - halfBase;
    y2 = centerY + normalizedHeight / 2; // Bottom left
    x3 = centerX + halfBase;
    y3 = centerY + normalizedHeight / 2; // Bottom right
  } else {
    // Scalene triangle: all sides different
    x1 = centerX;
    y1 = centerY - normalizedHeight / 2; // Top vertex
    x2 = centerX - halfBase;
    y2 = centerY + normalizedHeight / 2; // Bottom left
    x3 = centerX + halfBase * 0.7;
    y3 = centerY + normalizedHeight / 2; // Bottom right (offset)
  }
  
  // Label positions
  const baseLabelX = centerX;
  const baseLabelY = centerY + normalizedHeight / 2 + 8; // Below the base
  const heightLabelX = centerX - halfBase - 5; // To the left
  const heightLabelY = centerY;
  
  // Hypotenuse label position (for right triangles)
  // Hypotenuse goes from vertex 3 (top-left) to vertex 2 (bottom-right)
  const hypotenuseMidX = (x3 + x2) / 2;
  const hypotenuseMidY = (y3 + y2) / 2;
  // Position label perpendicular to the hypotenuse, slightly outside
  const dx = x2 - x3;
  const dy = y2 - y3;
  const len = Math.sqrt(dx * dx + dy * dy);
  const perpX = -dy / len; // Perpendicular unit vector
  const perpY = dx / len;
  const hypotenuseLabelX = hypotenuseMidX + perpX * 6 + 20; // 6 units offset + 20 to the right
  const hypotenuseLabelY = hypotenuseMidY + perpY * 6;
  
  // For equilateral, show side length instead of base/height
  const showSideLabel = triangleType === 'equilateral' && side !== undefined;
  // For right triangles, show hypotenuse if requested
  const showHypotenuseLabel = triangleType === 'right' && showHypotenuse && hypotenuse !== undefined;
  
  // Calculate angle positions (arcs near vertices)
  const getAngleLabelPosition = (vertexX: number, vertexY: number, angle: number, offset: number = 6) => {
    // Position angle label slightly away from vertex
    const angleRad = (angle * Math.PI) / 180;
    return {
      x: vertexX + Math.cos(angleRad) * offset,
      y: vertexY - Math.sin(angleRad) * offset
    };
  };
  
  // Default angles based on triangle type if not provided
  let angle1 = angles?.angle1;
  let angle2 = angles?.angle2;
  let angle3 = angles?.angle3;
  
  if (withAngles && !angles) {
    // Set default angles based on triangle type
    if (triangleType === 'equilateral') {
      angle1 = 60;
      angle2 = 60;
      angle3 = 60;
    } else if (triangleType === 'right') {
      angle1 = 90; // Right angle at vertex 1 (bottom left)
      angle2 = 45;
      angle3 = 45;
    } else if (triangleType === 'isosceles') {
      angle1 = 40; // Apex angle
      angle2 = 70;
      angle3 = 70;
    } else {
      // Scalene
      angle1 = 50;
      angle2 = 60;
      angle3 = 70;
    }
  }
  
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', maxWidth: '320px', height: '320px', display: 'block' }}
      >
        {/* Triangle fill */}
        <polygon
          points={`${x1},${y1} ${x2},${y2} ${x3},${y3}`}
          fill={color}
          opacity={0.4}
        />
        
        {/* Triangle outline */}
        <polygon
          points={`${x1},${y1} ${x2},${y2} ${x3},${y3}`}
          fill="none"
          stroke={strokeColor}
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
        
        {/* Corner points */}
        <circle cx={x1} cy={y1} r={1.2} fill={strokeColor} />
        <circle cx={x2} cy={y2} r={1.2} fill={strokeColor} />
        <circle cx={x3} cy={y3} r={1.2} fill={strokeColor} />
        
        {/* Angle labels */}
        {withAngles && angle1 !== undefined && angle2 !== undefined && angle3 !== undefined && (
          <>
            {triangleType === 'right' ? (
              // Special positioning for right triangles
              <>
                {/* Right angle (90°) at vertex 1 (bottom-left) - outside, below the marker */}
                <text
                  x={x1 + 1}
                  y={y1 + 5}
                  fontSize={4}
                  fill={strokeColor}
                  textAnchor="start"
                  fontWeight={600}
                >
                  {angle1}°
                </text>
                {/* Bottom-right angle (30°) - outside, below base */}
                <text
                  x={x2 + 2}
                  y={y2 + 6}
                  fontSize={4}
                  fill={strokeColor}
                  textAnchor="middle"
                  fontWeight={600}
                >
                  {angle2}°
                </text>
                {/* Top-left angle (60°) - inside, near vertex */}
                <text
                  x={x3 + 1}
                  y={y3 - 3}
                  fontSize={4}
                  fill={strokeColor}
                  textAnchor="start"
                  fontWeight={600}
                >
                  {angle3}°
                </text>
              </>
            ) : (
              // Positioning for other triangle types
              <>
                {/* Angle 1 (at vertex 1) */}
                <text
                  x={x1 - 4}
                  y={y1 - 4}
                  fontSize={4}
                  fill={strokeColor}
                  textAnchor="middle"
                  fontWeight={600}
                >
                  {angle1}°
                </text>
                {/* Angle 2 (at vertex 2) */}
                <text
                  x={x2 - 4}
                  y={y2 + 6}
                  fontSize={4}
                  fill={strokeColor}
                  textAnchor="middle"
                  fontWeight={600}
                >
                  {angle2}°
                </text>
                {/* Angle 3 (at vertex 3) */}
                <text
                  x={x3 + 4}
                  y={y3 + 6}
                  fontSize={4}
                  fill={strokeColor}
                  textAnchor="middle"
                  fontWeight={600}
                >
                  {angle3}°
                </text>
              </>
            )}
          </>
        )}
        
        {/* Labels - omit when showSideLabels is false (e.g. "angles only" problems) */}
        {showSideLabels && (
          showSideLabel ? (
            // Equilateral triangle: show side length
            <text
              x={baseLabelX}
              y={baseLabelY}
              fontSize={4.5}
              fill="#374151"
              textAnchor="middle"
              fontWeight={600}
            >
              s = {side} {unit}
            </text>
          ) : showHypotenuseLabel ? (
            // Right triangle: show hypotenuse
            <text
              x={hypotenuseLabelX}
              y={hypotenuseLabelY}
              fontSize={4.5}
              fill="#374151"
              textAnchor="middle"
              fontWeight={600}
            >
              c = {hypotenuse} {unit}
            </text>
          ) : (
            <>
              {/* Base label */}
              <text
                x={baseLabelX}
                y={baseLabelY}
                fontSize={4.5}
                fill="#374151"
                textAnchor="middle"
                fontWeight={600}
              >
                b = {base} {unit}
              </text>

              {/* Height label */}
              <text
                x={heightLabelX}
                y={heightLabelY}
                fontSize={4.5}
                fill="#374151"
                textAnchor="end"
                fontWeight={600}
              >
                h = {height} {unit}
              </text>
            </>
          )
        )}
      </svg>
    </Box>
  );
};

export default Triangle;
